import { useMemo } from 'react'

import { Size } from '@syconium/little-miss-figgy'

import { usePortal } from '../../../../../../app/_providers/PortalProvider.client'
import { useSiteWideMessaging } from '../../../../../../app/_providers/SiteWideMessagingProvider.client'
import {
  AvailableProducts,
  ColorPriceRanges,
  deriveFitNamesMap,
  isColorFitComboValid,
} from '../../../../../../lib/utils'
import { CollectionLink, ProductAsset, Variant } from '../../../../../../types/graphql'
import { ProductPageProduct } from '../../hooks/types/ProductPageProduct'
import { ProductPageProductGroup } from '../../hooks/types/ProductPageProductGroup'
import { getDiscountPrice, getPrice } from '../../hooks/types/utils'
import { useProductPagePortal } from '../../hooks/useProductPagePortal'

/**
 * @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is.
 */
export type UseProductDetailsOpts = {
  /** @deprecated */
  featuredProduct: ProductPageProduct
  /** @deprecated */
  productGroup: ProductPageProductGroup
  /** @deprecated */
  selectedVariant?: Variant
}

/**
 * @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is.
 */
export type UseProductDetailsResponse = {
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  availableProducts: AvailableProducts
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  category: string | undefined
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  collectionLinks: CollectionLink[]
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  colorGroups: ReturnType<(typeof ProductPageProductGroup)['getColorGroupsWithUnavailable']>
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  colorPriceRanges: ColorPriceRanges
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  portalColorGroups: ReturnType<(typeof ProductPageProductGroup)['getColorGroupsWithUnavailable']>
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  discountPrice: number | undefined
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  discountPriceExplicitFmt: string | undefined
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  featuredProduct: ProductPageProduct
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  fitKeys: string[]
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  fitNames: ReturnType<typeof deriveFitNamesMap>
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  isPortalColorSelected: boolean
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  isProductEmbroiderable: boolean
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  isProductFinalSale: boolean
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  isProductOnWaitlist: boolean
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  price: ReturnType<typeof getPrice>['price']
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  priceExplicitFmt: ReturnType<typeof getPrice>['priceExplicitFmt']
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  productGroup: ProductPageProductGroup
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  siteWidePromoText: string | undefined
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  selectedColorKey: string | undefined
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  selectedSize: Size | undefined
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  selectedVariant: Variant | undefined
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  sizes: Size[]
  /** @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is. */
  wwltImage: ProductAsset | undefined
}

/**
 * @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is.
 */
export type MakeUpdateQueryParam = <S>(
  key: string,
  extractValue: (selection: S) => string
) => (selection: S) => void

/**
 * @deprecated Use catalog api data rather than Zidi. It doesn't require computing fields like this. It is more ergonomic as is.
 */
export const useProductDetails = (input: UseProductDetailsOpts): UseProductDetailsResponse => {
  // Get the current featured product/product group/selected variant from input
  const { featuredProduct, productGroup, selectedVariant } = input

  const { portal } = usePortal()

  // string fixtures related to the product details section
  const { siteWidePromotionMessage: siteWidePromo } = useSiteWideMessaging()

  // The products that are available for the current product group
  const availableProducts = useMemo(
    () => ProductPageProductGroup.getAvailableProducts(productGroup.productsForAvailability),
    [productGroup]
  )

  // The product category for the currently active featured product
  const category = useMemo(
    () => selectedVariant?.category ?? (featuredProduct?.variants ?? [])[0]?.category,
    [featuredProduct?.variants, selectedVariant?.category]
  )

  const collectionLinks = useMemo(
    () => featuredProduct?.collectionLinks.nodes ?? [],
    [featuredProduct?.collectionLinks.nodes]
  )

  const portalColorGroups = useMemo(
    () =>
      ProductPageProductGroup.getColorGroupsWithUnavailable(
        productGroup?.portalColorGroups ?? [],
        productGroup.productsForAvailability,
        featuredProduct?.rawFit,
        selectedVariant?.size
      ),
    [featuredProduct?.rawFit, productGroup, selectedVariant?.size]
  )

  const colorGroups = useMemo(() => {
    const groups = ProductPageProductGroup.getColorGroupsWithUnavailable(
      productGroup?.colorGroups ?? [],
      productGroup.productsForAvailability,
      featuredProduct?.rawFit,
      selectedVariant?.size
    )

    if (!portalColorGroups || portalColorGroups.length === 0) return groups

    // Exclude the colors/color groups that are present in the portal color groups response
    return ProductPageProductGroup.excludeMatchingColors(groups, portalColorGroups)
  }, [featuredProduct?.rawFit, productGroup, selectedVariant?.size, portalColorGroups])

  const colorPriceRanges = useMemo(() => {
    return ProductPageProductGroup.getColorPriceRanges(productGroup.productsForAvailability)
  }, [productGroup.productsForAvailability])

  const { discountPrice, discountPriceExplicitFmt } = useMemo(
    () => getDiscountPrice({ selectedVariant, featuredProduct }),
    [featuredProduct, selectedVariant]
  )

  const fitNames = useMemo(() => deriveFitNamesMap(productGroup), [productGroup])

  const fitKeys = useMemo(() => {
    return productGroup?.rawFits.filter(fitKey =>
      isColorFitComboValid({
        availableProducts,
        colorKey: ProductPageProduct.getSelectedColorKey(featuredProduct) ?? '',
        fitKey,
      })
    )
  }, [availableProducts, featuredProduct, productGroup?.rawFits])

  const { isPortalColorSelected } = useProductPagePortal({
    portalColorGroups: productGroup.portalColorGroups ?? [],
    selectedColorKey: ProductPageProduct.getSelectedColorKey(featuredProduct),
  })

  const { price, priceExplicitFmt } = useMemo(
    () => getPrice({ selectedVariant, featuredProduct }),
    [featuredProduct, selectedVariant]
  )

  const sizes = useMemo(
    () => ProductPageProduct.getSizes(featuredProduct, productGroup.sizes),
    [featuredProduct, productGroup.sizes]
  )

  const selectedSize: Size | undefined = useMemo(() => {
    return sizes.find(size => size.shorthand === selectedVariant?.size)
  }, [selectedVariant, sizes])

  const selectedColorKey = useMemo(
    () => ProductPageProduct.getSelectedColorKey(featuredProduct),
    [featuredProduct]
  )

  // Get the promotional text to display in the details section.
  // Returns "undefined" if no promotional text should be rendered.
  const siteWidePromoText = useMemo(() => {
    // If a portal color is selected & the portal has an active discount percentage, don't display sitewide promo text
    if (isPortalColorSelected && portal?.discountPercentage) return undefined
    // If the product is site-wide promo eligible, display the site-wide promo text
    if (featuredProduct.swPromoEligible && siteWidePromo) return siteWidePromo
    return undefined
  }, [portal, isPortalColorSelected, featuredProduct.swPromoEligible, siteWidePromo])

  const wwltImage = useMemo(
    () => featuredProduct.images?.nodes.find(image => image.kind === 'wwlt'),
    [featuredProduct.images?.nodes]
  )

  const isProductFinalSale = ProductPageProduct.isProductFinalSale(featuredProduct)

  const isProductEmbroiderable = ProductPageProduct.isProductEmbroiderable(featuredProduct)

  const isProductOnWaitlist =
    featuredProduct.waitlistable && (selectedSize?.soldOut || featuredProduct.soldOut)

  return {
    availableProducts,
    category,
    collectionLinks,
    colorGroups,
    colorPriceRanges,
    portalColorGroups,
    discountPrice,
    discountPriceExplicitFmt,
    featuredProduct,
    fitKeys,
    fitNames,
    isPortalColorSelected,
    isProductEmbroiderable,
    isProductFinalSale,
    isProductOnWaitlist,
    price,
    priceExplicitFmt,
    productGroup,
    siteWidePromoText,
    selectedColorKey,
    selectedSize,
    selectedVariant,
    sizes,
    wwltImage,
  }
}
